import { useEffect, useState } from "react";

/**
 * Downloadable 1080x1080 promo banners for JobGrid.
 *
 * Renders three variants side-by-side so a marketer can pick their
 * favorite and export the PNG (right-click → save as, or use the
 * screenshot tool during preview). Each variant is a self-contained
 * 1080x1080 <section> — no external dependencies, no auth needed.
 *
 * URL: /promo (accessible without login).
 */

const HEADLINE = "Jouw AI-carrière-assistent";
const SUB = "CV's · Brieven · Video-pitches";
const TAG = "Alles in één overzicht — tot match-score verbetering";
const FEATURES = [
  { label: "AI CV Builder", icon: "📄" },
  { label: "Motivatiebrieven", icon: "✉️" },
  { label: "Video-pitch", icon: "🎬" },
  { label: "Match-score+", icon: "🎯" },
  { label: "Interview prep", icon: "💬" },
  { label: "Recruiter-vragenlijst", icon: "📋" },
];

// Shared JobGrid grid-mark used as brand anchor across all variants.
function GridMark({ size = 42, gap = 4 }) {
  const cell = (size - gap * 2) / 3;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <defs>
        <linearGradient id="jgGoldPromo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFD989" />
          <stop offset="50%" stopColor="#F1B546" />
          <stop offset="100%" stopColor="#B77A19" />
        </linearGradient>
      </defs>
      {[0, 1, 2].flatMap((r) =>
        [0, 1, 2].map((c) => (
          <rect
            key={`${r}-${c}`}
            x={c * (cell + gap)}
            y={r * (cell + gap)}
            width={cell}
            height={cell}
            fill="url(#jgGoldPromo)"
            rx="1"
          />
        ))
      )}
    </svg>
  );
}

function Wordmark({ light = false, size = 56 }) {
  const color = light ? "#FFF6E4" : "#1A1815";
  const tag = light ? "#FFDDA2" : "#4A3F28";
  return (
    <div className="inline-flex flex-col items-center leading-none">
      <span
        style={{ color, fontFamily: "'Cormorant Garamond', 'Cormorant', serif", fontSize: size, fontWeight: 500, letterSpacing: "-0.02em" }}
        className="inline-flex items-center gap-2"
      >
        <span>J</span>
        <GridMark size={size * 0.7} gap={size * 0.06} />
        <span>BGRID</span>
      </span>
      <span
        style={{ color: tag, fontFamily: "'Cormorant Garamond', serif", fontSize: size * 0.22, fontStyle: "italic", letterSpacing: "0.15em" }}
        className="mt-1"
      >
        No.&nbsp;1
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant A — Coral gradient, big centered headline, floating chips  */
/* ------------------------------------------------------------------ */
function VariantA() {
  return (
    <div
      data-testid="promo-variant-a"
      className="relative overflow-hidden"
      style={{
        width: 1080,
        height: 1080,
        background:
          "radial-gradient(120% 90% at 15% 10%, #FFC98A 0%, transparent 55%)," +
          "radial-gradient(120% 90% at 90% 100%, #FF7A6B 0%, transparent 60%)," +
          "linear-gradient(135deg, #FFE8CE 0%, #FFB199 45%, #FF6F91 100%)",
      }}
    >
      {/* Decorative blur blobs */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -120,
          right: -160,
          width: 520,
          height: 520,
          borderRadius: "50%",
          background: "radial-gradient(circle, #FFD16B 0%, transparent 70%)",
          filter: "blur(6px)",
          opacity: 0.85,
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          bottom: -140,
          left: -140,
          width: 460,
          height: 460,
          borderRadius: "50%",
          background: "radial-gradient(circle, #6DE0C6 0%, transparent 70%)",
          filter: "blur(4px)",
          opacity: 0.8,
        }}
      />
      {/* Subtle 3×3 grid confetti — nods to JobGrid mark */}
      <svg
        aria-hidden
        style={{ position: "absolute", inset: 0, opacity: 0.13 }}
        width="100%"
        height="100%"
      >
        <defs>
          <pattern id="pgA" x="0" y="0" width="90" height="90" patternUnits="userSpaceOnUse">
            <rect x="10" y="10" width="10" height="10" fill="#1A1815" />
            <rect x="35" y="10" width="10" height="10" fill="#1A1815" />
            <rect x="60" y="10" width="10" height="10" fill="#1A1815" />
            <rect x="10" y="35" width="10" height="10" fill="#1A1815" />
            <rect x="35" y="35" width="10" height="10" fill="#1A1815" />
            <rect x="60" y="35" width="10" height="10" fill="#1A1815" />
            <rect x="10" y="60" width="10" height="10" fill="#1A1815" />
            <rect x="35" y="60" width="10" height="10" fill="#1A1815" />
            <rect x="60" y="60" width="10" height="10" fill="#1A1815" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#pgA)" />
      </svg>

      <div className="relative h-full w-full flex flex-col justify-between p-20">
        {/* Header row: logo + tagline pill */}
        <div className="flex items-start justify-between">
          <Wordmark size={64} />
          <div
            style={{
              background: "rgba(26,24,21,0.9)",
              color: "#FFE7C4",
              padding: "12px 22px",
              borderRadius: 999,
              fontFamily: "monospace",
              fontSize: 18,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
            }}
          >
            No. 1 SaaS
          </div>
        </div>

        {/* Center headline */}
        <div className="flex-1 flex flex-col justify-center">
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 108,
              lineHeight: 1.02,
              color: "#1A1815",
              letterSpacing: "-0.03em",
              fontWeight: 500,
              marginBottom: 24,
            }}
          >
            Jouw&nbsp;AI-
            <br />
            <span style={{ fontStyle: "italic", background: "linear-gradient(90deg,#B45309,#DC2626)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              carrière-assistent
            </span>
          </div>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 24,
              color: "#3A2F26",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              marginBottom: 20,
            }}
          >
            {SUB}
          </div>
          <div
            style={{
              fontSize: 32,
              color: "#1A1815",
              maxWidth: 780,
              lineHeight: 1.35,
              fontWeight: 500,
            }}
          >
            {TAG}
          </div>
        </div>

        {/* Feature chips */}
        <div className="flex flex-wrap gap-3">
          {FEATURES.map((f) => (
            <div
              key={f.label}
              style={{
                background: "rgba(255,255,255,0.85)",
                border: "1.5px solid #1A1815",
                color: "#1A1815",
                padding: "12px 22px",
                borderRadius: 999,
                fontSize: 22,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 6px 20px rgba(180,83,9,0.18)",
              }}
            >
              <span style={{ fontSize: 26 }}>{f.icon}</span>
              {f.label}
            </div>
          ))}
        </div>

        {/* Footer URL */}
        <div className="flex items-center justify-between pt-6">
          <div style={{ fontFamily: "monospace", fontSize: 22, letterSpacing: "0.3em", color: "#1A1815", textTransform: "uppercase" }}>
            jobgrid.nl
          </div>
          <div style={{ fontSize: 18, color: "#3A2F26", fontStyle: "italic" }}>
            Van vacature tot aanbod
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant B — Dark plum with neon accents + feature grid             */
/* ------------------------------------------------------------------ */
function VariantB() {
  return (
    <div
      data-testid="promo-variant-b"
      className="relative overflow-hidden"
      style={{
        width: 1080,
        height: 1080,
        background:
          "radial-gradient(80% 60% at 100% 0%, #7C3AED 0%, transparent 55%)," +
          "radial-gradient(70% 60% at 0% 100%, #F97316 0%, transparent 55%)," +
          "linear-gradient(160deg, #1E0B3B 0%, #2C0F55 55%, #3D1170 100%)",
        color: "#FFF6E4",
      }}
    >
      {/* Neon streaks */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: 200,
          right: -220,
          width: 800,
          height: 160,
          background: "linear-gradient(90deg, transparent, #F472B6, transparent)",
          transform: "rotate(-14deg)",
          opacity: 0.55,
          filter: "blur(2px)",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          bottom: 180,
          left: -220,
          width: 800,
          height: 140,
          background: "linear-gradient(90deg, transparent, #34D399, transparent)",
          transform: "rotate(-14deg)",
          opacity: 0.5,
          filter: "blur(2px)",
        }}
      />

      <div className="relative h-full w-full flex flex-col p-20">
        {/* Header */}
        <div className="flex items-start justify-between">
          <Wordmark size={60} light />
          <div
            style={{
              background: "linear-gradient(90deg,#F472B6,#FBBF24)",
              color: "#1A1815",
              padding: "12px 22px",
              borderRadius: 999,
              fontFamily: "monospace",
              fontSize: 18,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            AI-powered
          </div>
        </div>

        {/* Headline */}
        <div style={{ marginTop: 60 }}>
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 118,
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              fontWeight: 500,
              marginBottom: 22,
            }}
          >
            Jouw&nbsp;AI-
            <br />
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(90deg,#FBBF24,#F472B6,#34D399)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              carrière-assistent
            </span>
          </div>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 22,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: "#FBBF24",
              marginBottom: 14,
            }}
          >
            {SUB}
          </div>
          <div style={{ fontSize: 30, color: "#FFE7C4", maxWidth: 800, lineHeight: 1.35 }}>
            {TAG}
          </div>
        </div>

        {/* Feature grid */}
        <div style={{ marginTop: "auto", marginBottom: 40 }} className="grid grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div
              key={f.label}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(251,191,36,0.4)",
                borderRadius: 20,
                padding: "20px 22px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                backdropFilter: "blur(8px)",
              }}
            >
              <span style={{ fontSize: 34 }}>{f.icon}</span>
              <span style={{ fontSize: 22, fontWeight: 600, color: "#FFF6E4" }}>{f.label}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-white/15 pt-6">
          <div style={{ fontFamily: "monospace", fontSize: 22, letterSpacing: "0.3em", textTransform: "uppercase", color: "#FBBF24" }}>
            jobgrid.nl
          </div>
          <div style={{ fontSize: 18, fontStyle: "italic", color: "#FFE7C4" }}>
            Solliciteer slimmer, niet harder
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant C — AI-generated background + editorial overlay            */
/* ------------------------------------------------------------------ */
function VariantC() {
  return (
    <div
      data-testid="promo-variant-c"
      className="relative overflow-hidden"
      style={{
        width: 1080,
        height: 1080,
        backgroundImage: "url('/promo-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Dark bottom vignette for text legibility */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(26,24,21,0.05) 0%, rgba(26,24,21,0.15) 40%, rgba(26,24,21,0.75) 100%)",
        }}
      />
      {/* Top-left cream lozenge holding the wordmark */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: 60,
          background: "rgba(255,246,228,0.94)",
          padding: "22px 30px",
          borderRadius: 20,
          boxShadow: "0 10px 40px rgba(26,24,21,0.25)",
        }}
      >
        <Wordmark size={54} />
      </div>

      {/* Corner pill */}
      <div
        style={{
          position: "absolute",
          top: 84,
          right: 60,
          background: "#1A1815",
          color: "#FFDDA2",
          padding: "12px 22px",
          borderRadius: 999,
          fontFamily: "monospace",
          fontSize: 16,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
        }}
      >
        Nieuw · v2
      </div>

      {/* Bottom editorial block */}
      <div
        style={{
          position: "absolute",
          left: 60,
          right: 60,
          bottom: 60,
          color: "#FFF6E4",
        }}
      >
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 20,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "#FFDDA2",
            marginBottom: 14,
          }}
        >
          {SUB}
        </div>
        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 116,
            lineHeight: 0.98,
            letterSpacing: "-0.03em",
            fontWeight: 500,
            marginBottom: 22,
          }}
        >
          Jouw&nbsp;AI-
          <br />
          <span style={{ fontStyle: "italic", color: "#FBBF24" }}>carrière-assistent</span>
        </div>
        <div
          style={{
            fontSize: 30,
            color: "#FFF6E4",
            maxWidth: 900,
            lineHeight: 1.35,
            marginBottom: 30,
          }}
        >
          {TAG}
        </div>
        <div className="flex flex-wrap gap-3">
          {FEATURES.slice(0, 4).map((f) => (
            <div
              key={f.label}
              style={{
                background: "rgba(255,246,228,0.92)",
                color: "#1A1815",
                padding: "12px 20px",
                borderRadius: 999,
                fontSize: 20,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 24 }}>{f.icon}</span>
              {f.label}
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 26,
            fontFamily: "monospace",
            fontSize: 22,
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: "#FFDDA2",
          }}
        >
          jobgrid.nl
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page shell with variant switcher                                   */
/* ------------------------------------------------------------------ */
export default function PromoBanner() {
  // ?v=a|b|c isolates one variant so screenshots come out pixel-perfect.
  const [variant, setVariant] = useState("all");
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("v");
    if (q && ["a", "b", "c"].includes(q)) setVariant(q);
  }, []);

  const Variants = {
    a: <VariantA />,
    b: <VariantB />,
    c: <VariantC />,
  };

  // Load fonts once. Cormorant Garamond mirrors the app wordmark.
  useEffect(() => {
    if (document.getElementById("promo-font")) return;
    const link = document.createElement("link");
    link.id = "promo-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,500&display=swap";
    document.head.appendChild(link);
  }, []);

  if (variant !== "all") {
    // Isolated view — no chrome so the screenshot equals the banner.
    return (
      <div style={{ margin: 0, padding: 0, background: "#000" }} data-testid="promo-isolated">
        {Variants[variant]}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2ED] p-10">
      <div className="max-w-[1200px] mx-auto mb-10">
        <h1 className="text-3xl font-serif text-neutral-900 mb-2">JobGrid — Promobanners (1080×1080)</h1>
        <p className="text-neutral-700 max-w-2xl mb-6">
          Drie downloadbare varianten. Klik op een variant om hem geïsoleerd te
          openen (perfecte 1080×1080 render zonder chrome) en gebruik dan het
          screenshot-menu van je browser om de PNG op te slaan.
        </p>
        <div className="flex flex-wrap gap-3">
          <a href="/promo?v=a" data-testid="promo-open-a" className="px-4 py-2 bg-[#24211D] text-[#F9F8F6] font-mono text-xs uppercase tracking-[0.2em] rounded-full">Open Variant A</a>
          <a href="/promo?v=b" data-testid="promo-open-b" className="px-4 py-2 bg-[#24211D] text-[#F9F8F6] font-mono text-xs uppercase tracking-[0.2em] rounded-full">Open Variant B</a>
          <a href="/promo?v=c" data-testid="promo-open-c" className="px-4 py-2 bg-[#24211D] text-[#F9F8F6] font-mono text-xs uppercase tracking-[0.2em] rounded-full">Open Variant C</a>
        </div>
      </div>

      {/* Preview thumbnails, scaled to fit the page but with real 1080 canvas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1600px] mx-auto">
        {["a", "b", "c"].map((k) => (
          <div key={k} className="flex flex-col items-center">
            <div className="text-sm font-mono uppercase tracking-[0.25em] text-neutral-700 mb-3">
              Variant {k.toUpperCase()}
            </div>
            <div
              style={{
                width: 480,
                height: 480,
                overflow: "hidden",
                borderRadius: 12,
                boxShadow: "0 20px 60px rgba(26,24,21,0.18)",
              }}
            >
              <div style={{ transform: "scale(0.4444)", transformOrigin: "top left", width: 1080, height: 1080 }}>
                {Variants[k]}
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <a
                href={`/promo?v=${k}`}
                data-testid={`promo-open-${k}`}
                className="px-4 py-2 bg-white border border-[#24211D] text-[#24211D] font-mono text-xs uppercase tracking-[0.2em] rounded-full hover:bg-[#F5F2ED]"
              >
                Bekijk
              </a>
              <a
                href={`/promo-${k}.png`}
                download={`jobgrid-promo-${k}.png`}
                data-testid={`promo-download-${k}`}
                className="px-4 py-2 bg-[#24211D] text-[#F9F8F6] font-mono text-xs uppercase tracking-[0.2em] rounded-full hover:bg-[#3D3934]"
              >
                Download PNG
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-[900px] mx-auto mt-16 text-sm text-neutral-600 leading-relaxed">
        <p className="mb-2 font-medium text-neutral-800">Tip:</p>
        <p>
          De PNG's zijn op 2160×2160 gerenderd (2× device-pixel-ratio) — scherp op
          retina-schermen en print. Voor Instagram/LinkedIn kun je ze direct
          uploaden; de meeste platforms schalen automatisch naar 1080×1080. Wil je
          een aanpassing (tekst, kleur, andere feature-chips)? Zeg het maar, dan
          render ik ze opnieuw.
        </p>
      </div>
    </div>
  );
}
